import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ guest_name: "", guest_phone: "", guest_email: "", checkin: "", checkout: "", adults: 2, kids: 0, room_id: "deluxe", package_id: "room_only" });
  const [quote, setQuote] = useState(null);

  const load = () => api.get("/bookings").then(r => setBookings(r.data));
  useEffect(() => {
    load();
    api.get("/rooms").then(r => setRooms(r.data));
    api.get("/packages").then(r => setPackages(r.data));
  }, []);

  const previewQuote = async () => {
    if (!form.checkin || !form.checkout) { toast.error("Enter dates first"); return; }
    try {
      const r = await api.post("/pricing/quote", { ...form, kids_ages: Array(form.kids).fill(5) });
      setQuote(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const save = async () => {
    try {
      await api.post("/bookings", form);
      toast.success("Booking created");
      setOpen(false);
      setForm({ guest_name: "", guest_phone: "", guest_email: "", checkin: "", checkout: "", adults: 2, kids: 0, room_id: "deluxe", package_id: "room_only" });
      setQuote(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl">Bookings</h1>
          <p className="text-[#8A7970] mt-1">All confirmed stays.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="new-booking-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5}/> New Booking
        </Button>
      </div>

      <div className="bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 overflow-hidden">
        {bookings.length === 0 ? (
          <div className="p-10 text-center text-[#8A7970]">No bookings yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EB] text-[#8A7970]">
              <tr>
                <th className="text-left p-3 font-medium">Guest</th>
                <th className="text-left p-3 font-medium">Dates</th>
                <th className="text-left p-3 font-medium">Room / Package</th>
                <th className="text-left p-3 font-medium">Amount</th>
                <th className="text-left p-3 font-medium">Payment</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(b => (
                <tr key={b.id} className="border-t border-[#E7C2A6]/30" data-testid={`booking-row-${b.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{b.guest_name}</div>
                    <div className="text-xs text-[#8A7970]">{b.guest_phone}</div>
                  </td>
                  <td className="p-3 text-xs">{b.checkin} → {b.checkout}<br/><span className="text-[#8A7970]">{b.nights} nights · {b.adults}A + {b.kids}K</span></td>
                  <td className="p-3 text-xs capitalize">{b.room_id}<br/><span className="text-[#8A7970]">{b.package_id}</span></td>
                  <td className="p-3 font-serif">₹{b.total_amount.toLocaleString('en-IN')}<br/><span className="text-xs text-[#8A7970]">Adv: ₹{b.advance_amount.toLocaleString('en-IN')}</span></td>
                  <td className="p-3 text-xs capitalize">{b.payment_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="new-booking-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New Booking</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 py-2">
            <div><label className="text-xs text-[#8A7970]">Guest Name *</label><Input value={form.guest_name} onChange={(e)=>setForm({...form,guest_name:e.target.value})} data-testid="booking-name-input"/></div>
            <div><label className="text-xs text-[#8A7970]">Phone *</label><Input value={form.guest_phone} onChange={(e)=>setForm({...form,guest_phone:e.target.value})} data-testid="booking-phone-input"/></div>
            <div><label className="text-xs text-[#8A7970]">Email</label><Input value={form.guest_email} onChange={(e)=>setForm({...form,guest_email:e.target.value})}/></div>
            <div></div>
            <div><label className="text-xs text-[#8A7970]">Check-in *</label><Input type="date" value={form.checkin} onChange={(e)=>setForm({...form,checkin:e.target.value})} data-testid="booking-checkin-input"/></div>
            <div><label className="text-xs text-[#8A7970]">Check-out *</label><Input type="date" value={form.checkout} onChange={(e)=>setForm({...form,checkout:e.target.value})} data-testid="booking-checkout-input"/></div>
            <div><label className="text-xs text-[#8A7970]">Adults</label><Input type="number" min={1} value={form.adults} onChange={(e)=>setForm({...form,adults:parseInt(e.target.value)||1})}/></div>
            <div><label className="text-xs text-[#8A7970]">Kids</label><Input type="number" min={0} value={form.kids} onChange={(e)=>setForm({...form,kids:parseInt(e.target.value)||0})}/></div>
            <div>
              <label className="text-xs text-[#8A7970]">Room</label>
              <select className="w-full h-10 rounded-md border border-[#E7C2A6] bg-white px-3" value={form.room_id} onChange={(e)=>setForm({...form,room_id:e.target.value})} data-testid="booking-room-select">
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#8A7970]">Package</label>
              <select className="w-full h-10 rounded-md border border-[#E7C2A6] bg-white px-3" value={form.package_id} onChange={(e)=>setForm({...form,package_id:e.target.value})} data-testid="booking-package-select">
                {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          {quote && (
            <div className="bg-[#F5F2EB] p-4 rounded-lg text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{quote.subtotal.toLocaleString('en-IN')}</span></div>
              {quote.discount_amount > 0 && <div className="flex justify-between text-[#477A5D]"><span>Discount ({quote.discount_pct}% - {quote.applied_discounts.join(", ")})</span><span>- ₹{quote.discount_amount.toLocaleString('en-IN')}</span></div>}
              {quote.anniversary_savings > 0 && <div className="flex justify-between text-[#477A5D]"><span>Anniversary Savings</span><span>- ₹{quote.anniversary_savings.toLocaleString('en-IN')}</span></div>}
              <div className="flex justify-between font-serif text-lg pt-2 border-t border-[#E7C2A6]"><span>Total</span><span className="text-[#B84F3E]">₹{quote.final_total.toLocaleString('en-IN')}</span></div>
              <div className="text-xs text-[#8A7970]">Fixed Advance: ₹{quote.advance_50pct.toLocaleString('en-IN')} · {quote.no_gst_note}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={previewQuote} data-testid="preview-quote-btn">Preview Price</Button>
            <Button onClick={save} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="save-booking-btn">Save Booking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
